import PropTypes from 'prop-types';

/**
 * Initials of a person, displayed in the avatar of list rows.
 * @param {string} firstName - First name.
 * @param {string} lastName - Last name.
 * @returns {string} - Up to two uppercase letters.
 */
const getInitials = (firstName, lastName) =>
  `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();

/**
 * PersonCell component: avatar with initials, name and a secondary line.
 * @component
 * @param {Object} props - The component props.
 * @param {string} props.firstName - First name.
 * @param {string} props.lastName - Last name.
 * @param {string} props.name - Displayed name.
 * @param {string} props.subtitle - Secondary line (email, reference...).
 * @returns {JSX.Element} - The PersonCell component.
 */
export const PersonCell = ({ firstName, lastName, name, subtitle }) => (
  <div className="cell-person">
    <span className="cell-avatar">{getInitials(firstName, lastName)}</span>
    <TwoLinesCell main={name} sub={subtitle} />
  </div>
);

/**
 * TwoLinesCell component: a main line and a secondary muted line.
 * @component
 * @param {Object} props - The component props.
 * @param {string} props.main - Main line.
 * @param {string} props.sub - Secondary line.
 * @returns {JSX.Element} - The TwoLinesCell component.
 */
export const TwoLinesCell = ({ main, sub }) => (
  <div className="cell-lines">
    <span className="cell-main">{main || '-'}</span>
    {sub && <span className="cell-sub">{sub}</span>}
  </div>
);

PersonCell.propTypes = {
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  name: PropTypes.string,
  subtitle: PropTypes.string
};

PersonCell.defaultProps = {
  firstName: '',
  lastName: '',
  name: '',
  subtitle: ''
};

TwoLinesCell.propTypes = {
  main: PropTypes.string,
  sub: PropTypes.string
};

TwoLinesCell.defaultProps = {
  main: '',
  sub: ''
};
